import { APP_INITIALIZER, ErrorHandler, ModuleWithProviders, NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { TranslateModule } from "@ngx-translate/core";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { ReactiveFormsModule } from "@angular/forms";

// shared libraries
import { AiFeatureGateService, GemComponentsModule, MaterialModule } from "gematik-form-library";
import { ChatContainerComponent, MarkdownPipe } from "gematik-support-chatbot";

// components
import { GemFeedbackCategoryFormComponent } from "./gematik-feedback/components/gem-feedback-category-form/gem-feedback-category-form.component";
import { GemFeedbackGeneralFormComponent } from "./gematik-feedback/components/gem-feedback-general-form/gem-feedback-general-form.component";
import { GemFeedbackFeatureFormComponent } from "./gematik-feedback/components/gem-feedback-feature-form/gem-feedback-feature-form.component";
import { GemFeedbackIncidentFormComponent } from "./gematik-feedback/components/gem-feedback-incident-form/gem-feedback-incident-form.component";
import { GemFeedbackInquiryFormComponent } from "./gematik-feedback/components/gem-feedback-inquiry-form/gem-feedback-inquiry-form.component";
import { GematikAlertsComponent } from "./gematik-alerts/gematik-alerts.component";
import { GematikHeaderComponent } from "./gematik-header/gematik-header.component";

// containers
import { GemFeedbackComponent } from "./gematik-feedback/containers/gem-feedback.component";
import { GemFooterComponent } from "./gematik-footer/containers/gem-footer.component";
import { GemLoadingSpinnerComponent } from "./gematik-loading-spinner/containers/gem-loading-spinner/gem-loading-spinner.component";
import { GemLoadingSpinnerNoBackdropComponent } from "./gematik-loading-spinner/containers/gem-loading-spinner-no-backdrop/gem-loading-spinner-no-backdrop.component";
import { GematikTourOverlayComponent } from "./gematik-tour/containers/gematik-tour-overlay/gematik-tour-overlay.component";
import { GemSkeletonComponent } from "./gematik-skeleton/gem-skeleton.component";
import { GemSkeletonTableComponent } from "./gematik-skeleton/gem-skeleton-table.component";
import { GemSkeletonListComponent } from "./gematik-skeleton/gem-skeleton-list.component";
import { GemLoadingPlaceholderComponent } from "./gematik-skeleton/gem-loading-placeholder.component";

// services
import { LoadingService } from "./services/loading.service";
import { TimelineService } from "./services/timeline.service";
import { SnackBarService } from "./services/snack-bar.service";
import { PortalService } from "./services/portal.service";
import { EntraIdService } from "./services/entra-id.service";
import { UtilService } from "./services/util.service";
import { UNLEASH_APP_NAME, UNLEASH_PRODUCTION_MODE } from "./services/unleash-feature-flag.service";

// interceptors
import { HttpRequestInterceptor } from "./interceptors/http-request-interceptor";
import { JwtInterceptor } from "./interceptors/jwt-interceptor";

// guards
import * as fromGuards from "./guards";
import { GlobalErrorHandler } from "./errors/global-error-handler";
import { ErrorDialogService } from "../public-api";
import { GematikErrorDialogComponent } from "./gematik-error-dialog/gematik-error-dialog.component";

@NgModule({
  declarations: [
    GemFeedbackComponent,
    GemFeedbackCategoryFormComponent,
    GemFeedbackGeneralFormComponent,
    GemFeedbackFeatureFormComponent,
    GemFeedbackIncidentFormComponent,
    GemFeedbackInquiryFormComponent,
    GemFooterComponent,
    GemLoadingSpinnerComponent,
    GemLoadingSpinnerNoBackdropComponent,
    GematikAlertsComponent,
    GematikHeaderComponent,
    GematikErrorDialogComponent,
    GematikTourOverlayComponent,
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    GemComponentsModule,
    MaterialModule,
    NgbModule,
    ChatContainerComponent,
    MarkdownPipe,
    GemSkeletonComponent,
    GemSkeletonTableComponent,
    GemSkeletonListComponent,
    GemLoadingPlaceholderComponent,
  ],
  exports: [
    GemSkeletonComponent,
    GemSkeletonTableComponent,
    GemSkeletonListComponent,
    GemLoadingPlaceholderComponent,
    GemFooterComponent,
    GemLoadingSpinnerComponent,
    GemLoadingSpinnerNoBackdropComponent,
    GematikAlertsComponent,
    GematikHeaderComponent,
    GematikTourOverlayComponent,
  ],
})
export class GematikSharedModule {
  static forRoot(unleashAppName?: string, production?: boolean): ModuleWithProviders<GematikSharedModule> {
    return {
      ngModule: GematikSharedModule,
      providers: [
        { provide: UNLEASH_APP_NAME, useValue: unleashAppName },
        { provide: UNLEASH_PRODUCTION_MODE, useValue: production },
        LoadingService,
        TimelineService,
        SnackBarService,
        PortalService,
        UtilService,
        EntraIdService,
        ErrorDialogService,
        ...fromGuards.guards,
        // {
        //   provide: ErrorHandler,
        //   useClass: GlobalErrorHandler,
        // },
        {
          provide: APP_INITIALIZER,
          useFactory: (aiFeatureGate: AiFeatureGateService) => () => aiFeatureGate.load(),
          deps: [AiFeatureGateService],
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: HttpRequestInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: JwtInterceptor,
          multi: true,
        },
      ],
    };
  }
}
